import { ReactNode } from "react";
import { Icon } from "Components/UI";
import RadioButtons from "Components/UI/RadioButtons";
import { IExistingFileInDestination } from "Data/interfaces/Upload/ICheckUploadFilesResponse";

export type TAlertMoveFilesType = 'version-down' |
  'version-equal' |
  'version-up' |
  'version-obsolete';

export interface IAlertMoveFilesData {
  type: TAlertMoveFilesType;
  filesData: IExistingFileInDestination;
  checkData?: IExistingFileInDestination & {
    fileNameNewRevision?: string;
    fileNameCopy?: string;
  };
}

export const getAlertMoveFilesTitle = (type: TAlertMoveFilesType) => {
  const opts = {
    'version-down': 'Versão inferior detectada na pasta de destino',
    'version-equal': 'Versão já existente na pasta de destino',
    'version-up': 'Versão superior detectada na pasta de destino',
    'version-obsolete': 'Mover para Arquivos Obsoletos?',
  };
  return opts[type];
}

export const getAlertMoveFilesDescriptionHeader = (
  type: TAlertMoveFilesType,
  v1?: string,
  v2?: string
): ReactNode => {
  const opts = {
    'version-down': (
      <div className="titleAlertWrapper">
        <span className="titleAlert">
          Você está movendo a versão <span className="titleAlertBold">“{v1}”</span> para uma pasta que contem a versão <span className="titleAlertBold">“{v2}”</span>. Ao continuar, a versão inferior será movida para a pasta <span className="titleAlertBold">“Arquivos obsoletos”</span>.
        </span>
      </div>
    ),
    'version-equal': (
      <div className="titleAlertWrapper">
        <span className="titleAlert">
          Você está movendo a versão <span className="titleAlertBold">“{v1}”</span> para uma pasta que já contem a versão <span className="titleAlertBold">“{v2}”</span>. Para continuar, selecione uma opção para renomear o novo arquivo:
        </span>
      </div>
    ),
    'version-up': (
      <div className="titleAlertWrapper">
        <span className="titleAlert">
          Você está movendo a versão <span className="titleAlertBold">“{v1}”</span> para uma pasta que contem a versão <span className="titleAlertBold">“{v2}”</span>. Ao continuar, o arquivo que está sendo movido será movido automaticamente para a pasta <span className="titleAlertBold">“Arquivos obsoletos”</span>.
        </span>
      </div>
    ),
    'version-obsolete': (
      <div className="titleAlertWrapper">
        <span className="titleAlert">
          Você está movendo a versão <span className="titleAlertBold">“{v1}”</span> para a pasta <span className="titleAlertBold">“Arquivos Obsoletos”</span>.
        </span>
        <span className="titleAlert">
          Isso não reinicia o versionamento, ou seja, no próximo upload, o sistema avisará que já existe uma versão e manterá a numeração atual.
        </span>
      </div>
    ),
  };
  return opts[type];
}

export const getAlertMoveFilesDescriptionFooter = (
  type: TAlertMoveFilesType,
  fileId: string,
  name1?: string,
  name2?: string,
  onValueChange?: (fileId: string, value: string) => void
): ReactNode => {
  const opts = {
    'version-down': (
      <div className="descriptionAlertWrapper gap">
        <Icon
          icon="obsoleteArchive"
          customSize={16}
          className="descriptionAlertIcon"
        />
        <span className="descriptionAlert">
          O arquivo da versão inferior estará acessível na pasta de arquivos obsoletos.
        </span>
      </div>
    ),
    'version-equal': (
      <div className="descriptionAlertWrapper">
        <div className="radioButtonsWrap">
          <RadioButtons
            options={[
              {
                value: name1 || '',
                label: (
                  <div className="newFileNameOption">
                    <div>{name1}</div>
                    <div className="titleOption">Nova revisão</div>
                  </div>
                ),
              },
              {
                value: name2 || '',
                label: (
                  <div className="newFileNameOption">
                    <div>{name2}</div>
                    <div className="titleOption">Cópia</div>
                  </div>
                ),
              },
            ]}
            onSelect={(value) => onValueChange && onValueChange(fileId, value as string)}
            defaultValue={name1}
          />
        </div>
      </div>
    ),
    'version-up': (
      <div className="descriptionAlertWrapper gap">
        <Icon icon="obsoleteArchive" customSize={16} className="descriptionAlertIcon" />
        <span className="descriptionAlert">
          O arquivo da versão inferior estará acessível na pasta de arquivos obsoletos.
        </span>
      </div>
    ),
    'version-obsolete': (
      <div className="descriptionAlertWrapper">
        <Icon icon="aviso" customSize={16} className="descriptionAlertIcon" />
        <span className="descriptionAlert">
          {"Para reiniciar o versionamento, é necessário excluir todas as versões antigas deste arquivo (inclusive as que estiverem em Arquivos obsoletos)."}
        </span>
      </div>
    ),
  };
  return opts[type];
}
